import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  sponsorPackages,
  totalGrants,
  type SponsorBreakdown,
} from '@/data/mock/initiative';

const SPONSOR_EMAIL = 'partnerships@champions.ps';
const CUSTOM_PKG_VALUE = 'Custom';

interface SponsorFormProps {
  customConfig?: SponsorBreakdown | null;
}

export function SponsorForm({ customConfig }: SponsorFormProps = {}) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang = isRTL ? 'ar' : 'en';
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const required = t('initiative.coaches.validation.required');
  const invalidEmail = t('initiative.coaches.validation.invalidEmail');

  const schema = z.object({
    organization: z.string().min(2, required),
    contactName: z.string().min(2, required),
    email: z.string().email(invalidEmail),
    phone: z.string().optional(),
    pkg: z.string().min(1, required),
    message: z.string().min(10, required).max(2000),
  });

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { pkg: '', message: '' },
  });

  const pkgValue = watch('pkg');

  // When the calculator hands us a config, prefill package + message.
  useEffect(() => {
    if (!customConfig) return;
    const grants = totalGrants(customConfig);
    if (grants === 0) return;
    const lines = [
      t('initiative.sponsor.form.customPrefillIntro'),
      '',
      `• ${t('initiative.sponsor.breakdownLabels.bihub')}: ${customConfig.bihub}`,
      `• ${t('initiative.sponsor.breakdownLabels.bilbao')}: ${customConfig.bilbao}`,
      `• ${t('initiative.sponsor.breakdownLabels.iknow')}: ${customConfig.iknow}`,
      `• ${t('initiative.sponsor.breakdownLabels.championsHub')}: ${customConfig.championsHub}`,
      '',
      `${t('initiative.sponsor.calculator.totalScholarships')}: ${grants}`,
      `${t('initiative.sponsor.priceLabel')}: ${t('initiative.sponsor.priceOnRequest')}`,
    ].join('\n');
    setValue('pkg', CUSTOM_PKG_VALUE, { shouldValidate: true });
    setValue('message', lines, { shouldValidate: true });
  }, [customConfig, setValue, t, lang]);

  const onSubmit = (values: FormValues) => {
    setSubmitting(true);
    const subject = `[Sponsorship Inquiry] ${values.organization} — ${values.pkg}`;
    const body = [
      `Organization: ${values.organization}`,
      `Contact: ${values.contactName}`,
      `Email: ${values.email}`,
      `Phone: ${values.phone || '—'}`,
      `Package of interest: ${values.pkg}`,
      '',
      'Message:',
      values.message,
    ].join('\n');

    // TODO: replace mailto with API submission to a real backend.
    window.location.href = `mailto:${SPONSOR_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    toast({
      title: t('initiative.sponsor.form.successTitle'),
      description: t('initiative.sponsor.form.successBody'),
    });
    reset();
    setSubmitting(false);
  };

  return (
    <motion.form
      id="sponsor-form"
      onSubmit={handleSubmit(onSubmit)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="grid grid-cols-1 md:grid-cols-2 gap-5"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="md:col-span-2">
        <Label htmlFor="organization">{t('initiative.sponsor.form.organization')}</Label>
        <Input id="organization" className="mt-2" {...register('organization')} />
        {errors.organization && (
          <p className="mt-1 text-xs text-destructive">{errors.organization.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="contactName">{t('initiative.sponsor.form.contactName')}</Label>
        <Input id="contactName" className="mt-2" {...register('contactName')} />
        {errors.contactName && (
          <p className="mt-1 text-xs text-destructive">{errors.contactName.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="email">{t('initiative.sponsor.form.email')}</Label>
        <Input id="email" type="email" className="mt-2" {...register('email')} />
        {errors.email && (
          <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="phone">{t('initiative.sponsor.form.phone')}</Label>
        <Input id="phone" type="tel" className="mt-2" {...register('phone')} />
      </div>

      <div>
        <Label htmlFor="pkg">{t('initiative.sponsor.form.package')}</Label>
        <Select value={pkgValue} onValueChange={(v) => setValue('pkg', v, { shouldValidate: true })}>
          <SelectTrigger id="pkg" className="mt-2">
            <SelectValue placeholder={t('initiative.sponsor.form.selectPackage')} />
          </SelectTrigger>
          <SelectContent>
            {sponsorPackages.map((p) => (
              <SelectItem key={p.id} value={p.name.en}>
                {p.name[lang]} — {p.totalGrants} {t('initiative.scholarships.grants')}
              </SelectItem>
            ))}
            <SelectItem value={CUSTOM_PKG_VALUE}>
              {t('initiative.sponsor.form.customPackage')}
            </SelectItem>
            <SelectItem value="open">
              {t('initiative.sponsor.form.anyPackage')}
            </SelectItem>
          </SelectContent>
        </Select>
        {errors.pkg && (
          <p className="mt-1 text-xs text-destructive">{errors.pkg.message}</p>
        )}
      </div>

      <div className="md:col-span-2">
        <Label htmlFor="message">{t('initiative.sponsor.form.message')}</Label>
        <Textarea
          id="message"
          rows={5}
          placeholder={t('initiative.sponsor.form.messagePlaceholder')}
          className="mt-2"
          {...register('message')}
        />
        {errors.message && (
          <p className="mt-1 text-xs text-destructive">{errors.message.message}</p>
        )}
      </div>

      <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
        <p className="text-xs text-muted-foreground inline-flex items-center gap-2">
          <Mail className="h-3.5 w-3.5" />
          {SPONSOR_EMAIL}
        </p>
        <Button type="submit" size="lg" className="btn-glow" disabled={submitting}>
          {submitting ? t('initiative.sponsor.form.submitting') : t('initiative.sponsor.form.submit')}
        </Button>
      </div>
    </motion.form>
  );
}
